import React, { useContext, useMemo, useState } from 'react'
import moment from 'moment'
import API from '../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../App'
// @MUI
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import RepeatIcon from '@mui/icons-material/Repeat'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'

// @MUIx
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'

import ConfirmRemoveStaff from './Shifts/ConfirmRemoveStaff'
import RepeatShiftPanel from './Shifts/RepeatShiftPanel'
import {
  buildOccurrences,
  buildShiftRepeatPayload,
  defaultShiftRepeat,
  getOccurrenceSlots,
  groupRepeatIssues,
  MAX_REPEAT_OCCURRENCES,
  MAX_REPEAT_SHIFTS,
} from './Shifts/shiftRepeat'
import ShiftSlotAssignments, {
  SlotAssignmentFields,
  emptySlot,
  getAssigneeRole,
  isSlotAssigned,
  resizeSlots,
  slotToPayload,
} from './Shifts/ShiftSlotAssignments'
import ShiftBonusEditor from '../../../components/Forms/ShiftBonusEditor'
import ShiftTravelEditor from '../../../components/Forms/ShiftTravelEditor'
import { getTravelPay, toTravelDraft } from '../../../helpers/shiftTravel'
import { formatMoney, getCurrencySymbol } from '../../../helpers/currency'
import { EventMetaContext } from './SingleEvent'

export const EVENT_ROLE_OPTIONS = [
  'Ambulance',
  'RRV',
  'Medical Centre',
  'Foot Patrol',
  'Response Team',
  'Manager',
  'Fixed Venue Cover',
]

const MAX_SHIFTS_PER_CREATE = 50

const formatDuration = (minutes) => {
  if (!Number.isFinite(minutes) || minutes <= 0) return '—'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return [hours ? `${hours}h` : '', mins ? `${mins}m` : '']
    .filter(Boolean)
    .join(' ')
}

const pluralShifts = (count) => `${count} shift${count === 1 ? '' : 's'}`

const clampQuantity = (value) =>
  Math.min(MAX_SHIFTS_PER_CREATE, Math.max(1, parseInt(value, 10) || 1))

// Issues come back flat from the server with an optional `slot` index; the
// update path and single-shift creates have no slot, so they land on 0.
const groupIssuesBySlot = (issues) =>
  (issues ?? []).reduce((grouped, issue) => {
    const key = issue?.slot ?? 0
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(issue)
    return grouped
  }, {})

// Flat card with an icon-led header - the same visual language as the
// SectionKit panels on the vehicle/base-location pages.
const Section = ({ icon, title, caption, action, children }) => (
  <Paper
    variant="outlined"
    sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, flex: 1 }}
  >
    <Stack direction="row" alignItems="center" spacing={1.25} mb={1.75}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.5,
          bgcolor: 'anMuted.main',
          color: 'contrast.main',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
          {title}
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
      {action}
    </Stack>
    <Stack spacing={1.75}>{children}</Stack>
  </Paper>
)

const StaffCanBookSwitch = ({ checked, onChange, disabled, caption }) => (
  <FormControlLabel
    sx={{ mx: 0, alignItems: 'flex-start' }}
    control={
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        size="small"
        disabled={disabled}
      />
    }
    label={
      <Box ml={0.5}>
        <Typography variant="body2" fontWeight={600}>
          Staff can book
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </Box>
    }
  />
)

const AddNewShift = ({
  existingShift,
  setExistingShift,
  setAddNew,
  hasPendingApplication,
  expandedDays,
  setExpandedDays,
}) => {
  const { eventMeta, loadEvent } = useContext(EventMetaContext)
  const hasExistingShift = Object.keys(existingShift).length > 0
  const { orgConfig } = useContext(TenantSettingsContext)
  const { setToast } = useContext(GlobalContext)
  const currency = getCurrencySymbol(orgConfig?.orgSettings?.currency)

  const [startTime, setStartTime] = useState(
    moment(hasExistingShift ? existingShift.start : eventMeta.startDate),
  )
  // Legacy events can be missing a valid endDate, and moment(undefined) is
  // "now" — usually before a future event's start, which would open the
  // dialog with a negative duration and Add shift disabled.
  const [endTime, setEndTime] = useState(() => {
    if (hasExistingShift) return moment(existingShift.end)
    const eventEnd = eventMeta.endDate ? moment(eventMeta.endDate) : null
    if (eventEnd?.isValid() && eventEnd.isAfter(moment(eventMeta.startDate))) {
      return eventEnd
    }
    return moment(eventMeta.startDate).add(1, 'hour')
  })
  const [selectedRole, setSelectedRole] = useState(
    existingShift?.clinicalRole ?? '',
  )
  const [shiftNote, setShiftNote] = useState(existingShift?.shiftNote ?? '')
  const [staffCanBook, setStaffCanBook] = useState(
    existingShift?.staffCanBook ?? true,
  )
  // Edit mode: the one saved assignee (guarded by application review +
  // removal confirmation). Add mode uses `slots` below instead.
  const [assignedTo, setAssignedTo] = useState(
    existingShift?.assignedTo
      ? {
          firstName: existingShift?.assignedTo?.userAccount?.firstName,
          lastName: existingShift?.assignedTo?.userAccount?.lastName,
          employee: {
            _id: existingShift?.assignedTo?._id,
            clinicalRole: existingShift?.assignedTo?.clinicalRole,
          },
          _id: existingShift?.assignedTo?._id,
        }
      : existingShift?.assignedSubcontractor
        ? {
            firstName:
              existingShift?.assignedSubcontractor?.userAccount?.firstName,
            lastName:
              existingShift?.assignedSubcontractor?.userAccount?.lastName,
            subcontractor: existingShift?.assignedSubcontractor,
            _id: existingShift?.assignedSubcontractor?.userAccount?._id,
            isSubcontractor: true,
          }
        : null,
  )
  const [assignedToSubcontractor, setAssignedToSubcontractor] = useState(
    !!existingShift?.assignedToSubcontractor,
  )
  const [eventAssignment, setEventAssignment] = useState(
    existingShift?.eventAssignment ?? '',
  )
  const [hourlyRate, setHourlyRate] = useState(
    existingShift?.hourlyRate ?? true,
  )
  const [rate, setRate] = useState(existingShift?.payRate ?? '')
  const [bonuses, setBonuses] = useState(
    (existingShift?.bonuses ?? []).map((bonus) => ({
      label: bonus.label ?? '',
      amount: bonus.amount ?? '',
    })),
  )
  const [travel, setTravel] = useState(toTravelDraft(existingShift?.travel))
  // Add mode: one slot per shift being created, each assignable on its own
  const [quantity, setQuantity] = useState(1)
  const [slots, setSlots] = useState([emptySlot()])
  const [lockedAlert, setLockedAlert] = useState(false)
  const [changesPresent, setChangesPresent] = useState(false)
  const [confirmRemoveStaff, setConfirmRemoveStaff] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [newAssignment, setNewAssignment] = useState(false)
  const [saving, setSaving] = useState(false)
  const [shiftAssignmentBlocked, setShiftAssignmentBlocked] = useState(null)
  // Duplicate dialog: copies of the *saved* shift, each assignable
  const [openDuplicateShiftPopup, setOpenDuplicateShiftPopup] = useState(false)
  const [duplicateNumber, setDuplicateNumber] = useState(1)
  const [duplicateSlots, setDuplicateSlots] = useState([emptySlot()])
  const [duplicateIssues, setDuplicateIssues] = useState(null)
  const [duplicating, setDuplicating] = useState(false)
  // Add mode: "Repeat this shift" - the same shift on several days
  const [repeat, setRepeatState] = useState(() =>
    defaultShiftRepeat(eventMeta.startDate, eventMeta.endDate),
  )
  const [repeatIssues, setRepeatIssues] = useState(null)

  const disableChanges =
    hasExistingShift &&
    (existingShift?.isPaid ||
      existingShift?.isVerified ||
      existingShift?.staffFeedback)
  const shiftHadExistingAssignment =
    !!existingShift?.assignedTo ||
    !!existingShift?.assignedSubcontractor ||
    !!existingShift?.assignedToSubcontractor
  const assignmentRequiresApplicationReview =
    hasPendingApplication && !shiftHadExistingAssignment
  const pendingApplicationCount = (existingShift?.applications ?? []).filter(
    (application) => application?.status === 'Pending',
  ).length
  const isMultiCreate = !hasExistingShift && quantity > 1
  const assignedSlotCount = slots.filter(isSlotAssigned).length
  const openSlotCount = quantity - assignedSlotCount

  const isRepeating = !hasExistingShift && repeat.enabled
  const setRepeat = (update) => {
    setRepeatState(update)
    setRepeatIssues(null)
    setChangesPresent(true)
  }
  const occurrences = useMemo(
    () =>
      isRepeating
        ? buildOccurrences({ start: startTime, end: endTime }, repeat)
        : [],
    [isRepeating, startTime, endTime, repeat],
  )
  const repeatShiftCount = occurrences.length * quantity
  const repeatAssignedCount = isRepeating
    ? occurrences
        .flatMap((occurrence, occurrenceIndex) =>
          getOccurrenceSlots(
            repeat,
            occurrence,
            occurrenceIndex,
            slots,
            quantity,
          ),
        )
        .filter(isSlotAssigned).length
    : 0
  const repeatInvalid =
    isRepeating &&
    (!occurrences.length ||
      occurrences.length > MAX_REPEAT_OCCURRENCES ||
      repeatShiftCount > MAX_REPEAT_SHIFTS)
  const repeatIssuesByOccurrence = useMemo(
    () => groupRepeatIssues(repeatIssues),
    [repeatIssues],
  )
  // How many shifts the Add button creates
  const createCount = isRepeating ? repeatShiftCount : quantity

  const getAssignmentID = (assignment) =>
    assignment?.subcontractor?._id ||
    assignment?.employee?._id ||
    assignment?.employee ||
    assignment?._id
  const isSameAssignment = (currentValue, nextValue) => {
    const currentID = getAssignmentID(currentValue)
    const nextID = getAssignmentID(nextValue)

    return currentID && nextID && String(currentID) === String(nextID)
  }
  const isClearingAssignment = (value) =>
    value === null || value === undefined || value === ''
  const blockPendingApplicationReassignment = () => {
    setToast({
      severity: 'error',
      message:
        'You must review pending applications before assigning this shift',
    })
  }

  const setShiftQuantity = (value) => {
    const next = clampQuantity(value)
    setQuantity(next)
    setSlots((prev) => resizeSlots(prev, next))
    setChangesPresent(true)
  }

  const setDuplicateCount = (value) => {
    const next = clampQuantity(value)
    setDuplicateNumber(next)
    setDuplicateSlots((prev) => resizeSlots(prev, next))
  }

  // Picking a person pre-fills the role from their clinical role. With one
  // shift it always follows the pick (as before); with several it only
  // fills an empty role so the first pick doesn't fight the second.
  const suggestRoleFrom = (value, { overwrite }) => {
    const role = getAssigneeRole(value)
    if (!role) return
    const next = overwrite ? role : selectedRole || role
    if (next === selectedRole) return
    setSelectedRole(next)
    // Picking a person before a role: also take the role's default rate,
    // as choosing the role from the list would have done
    const rateIsEmpty = rate === '' || rate === null || rate === undefined
    const defaultRate = orgConfig?.orgSettings?.defaultPayRates?.[next]
    if (rateIsEmpty && defaultRate !== undefined) setRate(defaultRate)
  }

  const issuesBySlot = useMemo(
    () => groupIssuesBySlot(shiftAssignmentBlocked),
    [shiftAssignmentBlocked],
  )
  const duplicateIssuesBySlot = useMemo(
    () => groupIssuesBySlot(duplicateIssues),
    [duplicateIssues],
  )
  const clearSlotIssues = (setter) => (slotIndex) =>
    setter((prev) => {
      const remaining = (prev ?? []).filter(
        (issue) => (issue?.slot ?? 0) !== slotIndex,
      )
      return remaining.length ? remaining : null
    })

  // Live summary for the header + pay estimate strip
  const durationMinutes =
    startTime?.isValid?.() && endTime?.isValid?.()
      ? endTime.diff(startTime, 'minutes')
      : 0
  const timesInvalid = durationMinutes <= 0
  const payEstimate = useMemo(() => {
    const numericRate = Number(rate) || 0
    const hours = durationMinutes > 0 ? durationMinutes / 60 : 0
    const base = hourlyRate ? numericRate * hours : numericRate
    const bonusTotal = bonuses.reduce(
      (sum, bonus) => sum + (Number(bonus?.amount) || 0),
      0,
    )
    const travelTotal = getTravelPay(travel)
    return {
      base,
      bonusTotal,
      travelTotal,
      total: base + bonusTotal + travelTotal,
    }
  }, [rate, hourlyRate, durationMinutes, bonuses, travel])
  const money = (value) => formatMoney(value, orgConfig?.orgSettings?.currency)

  const statusChips = []
  if (hasExistingShift) {
    if (existingShift?.isPaid) {
      statusChips.push({ label: 'Paid', color: 'success' })
    } else if (existingShift?.isVerified) {
      statusChips.push({ label: 'Verified', color: 'success' })
    } else if (existingShift?.staffFeedback) {
      statusChips.push({ label: 'Completed', color: 'info' })
    }
    if (shiftHadExistingAssignment) {
      statusChips.push({ label: 'Assigned', color: 'default' })
    } else if (existingShift?.staffCanBook) {
      statusChips.push({ label: 'Open vacancy', color: 'warning' })
    }
    if (pendingApplicationCount) {
      statusChips.push({
        label: `${pendingApplicationCount} pending application${
          pendingApplicationCount === 1 ? '' : 's'
        }`,
        color: 'contrast',
      })
    }
  }

  const closePopup = () => {
    setAddNew(false)
    setExistingShift?.({})
  }

  const handleAddShift = (e) => {
    e.preventDefault()
    if (disableChanges) {
      setLockedAlert(true)
      return
    }
    setSaving(true)
    setShiftAssignmentBlocked(null)
    const sharedFields = {
      clinicalRole: selectedRole,
      eventAssignment,
      shiftNote,
      staffCanBook,
      payRate: rate,
      hourlyRate,
      bonuses: bonuses.filter(
        (bonus) => bonus.amount !== '' && Number(bonus.amount) >= 0,
      ),
      // null clears paid travel; the server normalises the object form
      travel: travel ?? null,
    }
    if (isRepeating) {
      handleRepeatShift(sharedFields)
      return
    }
    API.post('/event-shift', {
      existingShift: hasExistingShift ? existingShift._id : null,
      event: eventMeta._id,
      start: startTime.toDate(),
      end: endTime.toDate(),
      ...sharedFields,
      ...(hasExistingShift
        ? {
            assignedTo: !assignedToSubcontractor
              ? (assignedTo?.employee?._id ?? null)
              : null,
            assignedSubcontractor: !assignedToSubcontractor
              ? (assignedTo?.subcontractor?._id ?? null)
              : null,
            assignedToSubcontractor,
            newAssignment,
          }
        : {
            quantity,
            assignments: slots.map(slotToPayload),
          }),
    })
      .then((res) => {
        if (
          res.status === 400 &&
          res?.data?.errorCode === 'shiftAssignmentBlocked'
        ) {
          setShiftAssignmentBlocked(res.data.issues)
          return
        }
        if (res.status === 200) {
          const preserveExpanded = expandedDays.slice()
          loadEvent()
          setExpandedDays(preserveExpanded)
          setAddNew(false)
          setToast({
            message: hasExistingShift
              ? 'Shift Successfully Updated'
              : quantity > 1
                ? `${pluralShifts(quantity)} added${
                    assignedSlotCount ? ` · ${assignedSlotCount} assigned` : ''
                  }`
                : 'Shift Successfully Added',
          })
          return
        }
        setToast({
          severity: 'error',
          message: `Unable to ${hasExistingShift ? 'update' : 'add'} shift`,
        })
      })
      .catch((e) => console.log(e))
      .finally(() => setSaving(false))
  }

  const handleRepeatShift = (sharedFields) => {
    setRepeatIssues(null)
    API.post(
      `/events/${eventMeta._id}/shifts/repeat`,
      buildShiftRepeatPayload({
        shift: sharedFields,
        repeat,
        occurrences,
        slots,
        quantity,
      }),
    )
      .then((res) => {
        if (
          res.status === 400 &&
          res?.data?.errorCode === 'shiftAssignmentBlocked'
        ) {
          setRepeatIssues(res.data.issues)
          setToast({
            severity: 'error',
            message: res.data.error ?? 'Some days need attention',
          })
          return
        }
        if (res.status === 200) {
          const preserveExpanded = expandedDays.slice()
          loadEvent()
          setExpandedDays(preserveExpanded)
          setAddNew(false)
          setToast({
            message: `${pluralShifts(res.data?.created ?? repeatShiftCount)} added across ${
              res.data?.days ?? occurrences.length
            } days${res.data?.assigned ? ` · ${res.data.assigned} assigned` : ''}`,
          })
          return
        }
        setToast({
          severity: 'error',
          message: res?.data?.error ?? 'Unable to add the repeated shifts',
        })
      })
      .catch((e) => console.log(e))
      .finally(() => setSaving(false))
  }

  const handleDeleteShift = () => {
    if (
      existingShift.assignedTo ||
      existingShift.assignedSubcontractor ||
      existingShift.assignedToSubcontractor
    ) {
      setToast({
        severity: 'error',
        message:
          'Cannot delete a shift that is assigned. Please remove the staff member first',
      })
    } else {
      API.delete('/event-shift/' + existingShift._id)
        .then((res) => {
          if (res.status === 200) {
            setToast({
              severity: 'info',
              message: 'Shift Deleted',
            })
            setAddNew(false)
            loadEvent()
          } else {
            setToast({
              severity: 'error',
              message: 'Unable to Delete Shift.',
            })
          }
        })
        .catch((e) => {
          console.log(e)
          setToast({
            severity: 'error',
            message: 'Unable to Delete Shift',
          })
        })
    }
  }

  // Copies take the saved shift's details (not unsaved edits in the form)
  // so the source and its copies always match on the board.
  const handleDuplicateShift = () => {
    setDuplicating(true)
    setDuplicateIssues(null)
    API.post('/event-shift', {
      event: existingShift.event?._id ?? existingShift.event ?? eventMeta._id,
      shiftType: existingShift.shiftType,
      start: existingShift.start,
      end: existingShift.end,
      clinicalRole: existingShift.clinicalRole,
      eventAssignment: existingShift.eventAssignment,
      shiftNote: existingShift.shiftNote,
      staffCanBook: existingShift.staffCanBook,
      payRate: existingShift.payRate,
      hourlyRate: existingShift.hourlyRate,
      bonuses: existingShift.bonuses ?? [],
      travel: existingShift.travel ?? null,
      quantity: duplicateNumber,
      assignments: duplicateSlots.map(slotToPayload),
    })
      .then((res) => {
        if (
          res.status === 400 &&
          res?.data?.errorCode === 'shiftAssignmentBlocked'
        ) {
          setDuplicateIssues(res.data.issues)
          return
        }
        if (res.status === 200) {
          const assignedCopies = duplicateSlots.filter(isSlotAssigned).length
          setToast({
            message: `${duplicateNumber === 1 ? 'Shift duplicated' : `${duplicateNumber} copies added`}${
              assignedCopies ? ` · ${assignedCopies} assigned` : ''
            }`,
          })
          setOpenDuplicateShiftPopup(false)
          setAddNew(false)
          loadEvent()
          return
        }
        setToast({ severity: 'error', message: 'Unable to duplicate shift' })
      })
      .catch((e) => console.log(e))
      .finally(() => setDuplicating(false))
  }

  const deleteDisabled =
    changesPresent ||
    existingShift.isVerified ||
    !!existingShift.assignedTo ||
    !!existingShift.assignedSubcontractor ||
    !!existingShift.assignedToSubcontractor ||
    !hasExistingShift ||
    hasPendingApplication

  const submitDisabled =
    saving ||
    !changesPresent ||
    !rate ||
    !selectedRole ||
    timesInvalid ||
    repeatInvalid

  const headerSummary = [
    startTime?.isValid?.() ? startTime.format('ddd D MMM') : null,
    startTime?.isValid?.() && endTime?.isValid?.()
      ? `${startTime.format('HH:mm')} – ${endTime.format('HH:mm')}`
      : null,
    durationMinutes > 0 ? formatDuration(durationMinutes) : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const footerCaption = lockedAlert
    ? 'This shift has been verified or paid so cannot be edited.'
    : disableChanges
      ? 'Read only'
      : changesPresent
        ? hasExistingShift
          ? 'Unsaved changes'
          : isRepeating
            ? `Creating ${pluralShifts(repeatShiftCount)} across ${occurrences.length} day${occurrences.length === 1 ? '' : 's'} · ${repeatAssignedCount} assigned · ${repeatShiftCount - repeatAssignedCount} open`
            : isMultiCreate
              ? `Creating ${pluralShifts(quantity)} · ${assignedSlotCount} assigned · ${openSlotCount} open`
              : `Creating ${assignedSlotCount ? 'an assigned shift' : 'an open vacancy'} on ${eventMeta?.title ?? 'this event'}`
        : hasExistingShift
          ? 'No changes yet'
          : `Creating a shift on ${eventMeta?.title ?? 'this event'}`

  const assignmentCaption = hasExistingShift
    ? shiftHadExistingAssignment
      ? 'Who is covering this shift'
      : 'Leave empty to advertise it as a vacancy'
    : isRepeating
      ? repeat.sameStaff
        ? 'Shifts needed each day — whoever you pick covers every day of the repeat'
        : 'Shifts needed each day — these picks are the first day; assign the rest under Repeat'
      : isMultiCreate
        ? 'Assign any or all of the shifts below — anything left open is advertised as a vacancy'
        : 'Leave empty to advertise it as a vacancy'

  const singleSlotIssues = issuesBySlot[0]

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
      <Box
        component="form"
        onSubmit={handleAddShift}
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        {/* ---- Header ---- */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            px: { xs: 2, sm: 3 },
            py: 2,
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <EventOutlinedIcon />
            </Box>
            <Stack flex={1} minWidth={0} gap={1}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                  {hasExistingShift
                    ? 'Edit shift'
                    : isRepeating
                      ? 'Add repeating shift'
                      : isMultiCreate
                        ? `Add ${quantity} shifts`
                        : 'Add shift'}
                </Typography>
                {statusChips.map((chip) => (
                  <Chip
                    key={chip.label}
                    label={chip.label}
                    color={chip.color}
                    size="small"
                    variant={chip.color === 'default' ? 'outlined' : 'filled'}
                  />
                ))}
                {isRepeating && (
                  <Chip
                    icon={<RepeatIcon />}
                    label={`${occurrences.length} day${occurrences.length === 1 ? '' : 's'} · ${pluralShifts(repeatShiftCount)}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {isMultiCreate && !isRepeating && (
                  <Chip
                    label={`${assignedSlotCount} assigned · ${openSlotCount} open`}
                    size="small"
                    variant="outlined"
                  />
                )}
                {disableChanges && (
                  <Chip
                    icon={<LockOutlinedIcon />}
                    label="Locked"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                title={`${eventMeta?.title ?? ''} · ${headerSummary}`}
              >
                {eventMeta?.title ? `${eventMeta.title} · ` : ''}
                {headerSummary}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} flexShrink={0}>
              {hasExistingShift && (
                <Tooltip title="More actions">
                  <IconButton
                    onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                    size="small"
                    aria-label="Shift actions"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Close">
                <IconButton
                  onClick={closePopup}
                  size="small"
                  aria-label="Close"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Menu
            open={!!menuAnchorEl}
            anchorEl={menuAnchorEl}
            onClose={() => setMenuAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                setDuplicateIssues(null)
                setOpenDuplicateShiftPopup(true)
              }}
            >
              <ListItemIcon>
                <ContentCopyOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Duplicate shift</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                handleDeleteShift()
              }}
              disabled={deleteDisabled}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <DeleteOutlineIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete shift</ListItemText>
            </MenuItem>
          </Menu>
        </Box>

        {/* ---- Body ---- */}
        <Box
          sx={{ px: { xs: 2, sm: 3 }, py: 2.5, bgcolor: 'background.default' }}
        >
          {hasExistingShift && disableChanges && (
            <Alert severity="info" icon={<LockOutlinedIcon />} sx={{ mb: 2 }}>
              <AlertTitle>This shift is locked</AlertTitle>
              Shifts become locked once staff mark them completed, or they are
              verified or paid. Undo those steps in Payroll to edit it again.
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* When */}
            <Grid item xs={12} lg={7} display="flex">
              <Section
                icon={<ScheduleOutlinedIcon fontSize="small" />}
                title="When"
                caption="The on-site window staff clock in and out against"
                action={
                  <Chip
                    label={formatDuration(durationMinutes)}
                    size="small"
                    color={timesInvalid ? 'error' : 'default'}
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                }
              >
                <Box>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <DateTimePicker
                        label="Start"
                        value={startTime}
                        onChange={(newValue) => {
                          setStartTime(newValue)
                          setChangesPresent(true)
                        }}
                        disabled={disableChanges}
                        slotProps={{
                          textField: { fullWidth: true, size: 'small' },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DateTimePicker
                        label="End"
                        value={endTime}
                        onChange={(newValue) => {
                          setChangesPresent(true)
                          setEndTime(newValue)
                        }}
                        minDateTime={startTime}
                        disabled={disableChanges}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: timesInvalid,
                            helperText: timesInvalid
                              ? 'End must be after the start'
                              : undefined,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
                {!disableChanges &&
                  eventMeta?.startDate &&
                  eventMeta?.endDate && (
                    <Box>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setStartTime(moment(eventMeta.startDate))
                          setEndTime(moment(eventMeta.endDate))
                          setChangesPresent(true)
                        }}
                      >
                        Use event times
                      </Button>
                    </Box>
                  )}
              </Section>
            </Grid>

            {/* Notes */}
            <Grid item xs={12} lg={5} display="flex">
              <Section
                icon={<NotesOutlinedIcon fontSize="small" />}
                title="Notes"
                caption="Visible to staff on the shift listing"
              >
                <TextField
                  value={shiftNote}
                  onChange={(e) => {
                    setChangesPresent(true)
                    setShiftNote(e.target.value)
                  }}
                  fullWidth
                  label="Shift notes"
                  placeholder="Meeting point, kit to bring, parking…"
                  autoComplete="off"
                  multiline
                  minRows={4}
                  size="small"
                  disabled={disableChanges}
                />
              </Section>
            </Grid>

            {/* Role & pay */}
            <Grid item xs={12} display="flex">
              <Section
                icon={<PaymentsOutlinedIcon fontSize="small" />}
                title="Role & pay"
                caption="Rate is pre-filled from the role's default; bonuses and travel are paid on top"
              >
                {/* Boxes isolate Grid gutters: Stack zeroes its children's
                    margins, which would strip the container's negative one */}
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <Stack spacing={1.75}>
                        <Box>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth size="small" required>
                                <InputLabel id="role-label">Role</InputLabel>
                                <Select
                                  labelId="role-label"
                                  label="Role"
                                  value={selectedRole}
                                  onChange={(e) => {
                                    setSelectedRole(e.target.value)
                                    setRate(
                                      (prev) =>
                                        orgConfig?.orgSettings
                                          ?.defaultPayRates?.[e.target.value] ??
                                        prev,
                                    )
                                    setHourlyRate(true)
                                    setChangesPresent(true)
                                  }}
                                  disabled={disableChanges}
                                >
                                  {(orgConfig?.orgSettings?.roles ?? []).map(
                                    (option) => (
                                      <MenuItem value={option} key={option}>
                                        {option}
                                      </MenuItem>
                                    ),
                                  )}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Autocomplete
                                value={eventAssignment}
                                onChange={(e, value) => {
                                  setEventAssignment(value ?? '')
                                  setChangesPresent(true)
                                }}
                                options={EVENT_ROLE_OPTIONS}
                                size="small"
                                disabled={disableChanges}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    onChange={(e) => {
                                      setEventAssignment(e.target.value)
                                      setChangesPresent(true)
                                    }}
                                    label="Event role"
                                    placeholder="e.g. Ambulance"
                                  />
                                )}
                                freeSolo
                              />
                            </Grid>
                          </Grid>
                        </Box>

                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1.5}
                          alignItems={{ sm: 'flex-start' }}
                        >
                          <TextField
                            value={rate}
                            onChange={(e) => {
                              setChangesPresent(true)
                              e.target.value >= 0 &&
                                setRate(parseFloat(e.target.value))
                            }}
                            onWheel={(e) => e.target.blur()}
                            label="Rate"
                            type="number"
                            size="small"
                            required
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  {currency}
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  {hourlyRate ? 'per hour' : 'per shift'}
                                </InputAdornment>
                              ),
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                            disabled={disableChanges}
                            fullWidth
                          />
                          <ToggleButtonGroup
                            value={hourlyRate ? 'hourly' : 'fixed'}
                            exclusive
                            size="small"
                            onChange={(e, value) => {
                              if (!value) return
                              setChangesPresent(true)
                              setHourlyRate(value === 'hourly')
                            }}
                            disabled={disableChanges}
                            sx={{ flexShrink: 0, height: 40 }}
                          >
                            <ToggleButton value="hourly" sx={{ px: 2 }}>
                              Hourly
                            </ToggleButton>
                            <ToggleButton value="fixed" sx={{ px: 2 }}>
                              Fixed
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Stack>

                        <ShiftBonusEditor
                          bonuses={bonuses}
                          onChange={(value) => {
                            setChangesPresent(true)
                            setBonuses(value)
                          }}
                          disabled={disableChanges}
                          currency={currency}
                        />
                        <ShiftTravelEditor
                          travel={travel}
                          onChange={(value) => {
                            setChangesPresent(true)
                            setTravel(value)
                          }}
                          disabled={disableChanges}
                          currency={currency}
                          defaultRate={
                            orgConfig?.orgSettings?.defaultTravelRate
                          }
                        />
                      </Stack>
                    </Grid>

                    {/* Pay estimate */}
                    <Grid item xs={12} md={5} display="flex">
                      <Box
                        sx={{
                          borderRadius: 1.5,
                          bgcolor: 'anMuted.main',
                          px: 2,
                          py: 1.75,
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          lineHeight={1.4}
                        >
                          Estimated pay per person
                        </Typography>
                        <Stack spacing={0.75} flex={1}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {hourlyRate
                                ? `${formatDuration(durationMinutes)} @ ${money(rate)}/hr`
                                : 'Fixed rate'}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {money(payEstimate.base)}
                            </Typography>
                          </Stack>
                          {payEstimate.bonusTotal > 0 && (
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              spacing={2}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Bonuses
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {money(payEstimate.bonusTotal)}
                              </Typography>
                            </Stack>
                          )}
                          {payEstimate.travelTotal > 0 && (
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              spacing={2}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Paid travel
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {money(payEstimate.travelTotal)}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                        <Divider sx={{ opacity: 0.6 }} />
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="baseline"
                          spacing={2}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            Total
                          </Typography>
                          <Typography variant="h6" fontWeight={700}>
                            {money(payEstimate.total)}
                          </Typography>
                        </Stack>
                        {createCount > 1 && (
                          <Typography variant="caption" color="text.secondary">
                            {money(payEstimate.total * createCount)} across all{' '}
                            {createCount} shifts if every slot is filled
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Section>
            </Grid>

            {/* Assignment */}
            <Grid item xs={12} display="flex">
              <Section
                icon={<PersonOutlineOutlinedIcon fontSize="small" />}
                title="Assignment"
                caption={assignmentCaption}
              >
                {hasExistingShift ? (
                  <>
                    <SlotAssignmentFields
                      slot={{ assignedTo, assignedToSubcontractor }}
                      disabled={disableChanges}
                      error={assignmentRequiresApplicationReview && !assignedTo}
                      helperText={
                        assignmentRequiresApplicationReview && assignedTo
                          ? 'Clear the current assignee to remove them; review pending applications before assigning someone else'
                          : assignmentRequiresApplicationReview
                            ? 'You must review pending applications first'
                            : ''
                      }
                      issues={singleSlotIssues}
                      onClearIssues={() => setShiftAssignmentBlocked(null)}
                      onAssigneeChange={(e, value) => {
                        if (
                          assignmentRequiresApplicationReview &&
                          !isClearingAssignment(value) &&
                          !isSameAssignment(assignedTo, value)
                        ) {
                          blockPendingApplicationReassignment()
                          return
                        }
                        if (existingShift?.assignedTo) {
                          setConfirmRemoveStaff(value ?? true)
                        } else {
                          setChangesPresent(true)
                          setAssignedTo(value)
                          setAssignedToSubcontractor(false)
                          suggestRoleFrom(value, { overwrite: true })
                          if (value !== null) setNewAssignment(true)
                        }
                      }}
                      onSubcontractorToggle={(checked) => {
                        if (
                          assignmentRequiresApplicationReview &&
                          checked &&
                          !assignedToSubcontractor
                        ) {
                          blockPendingApplicationReassignment()
                          return
                        }
                        setChangesPresent(true)
                        setAssignedToSubcontractor(checked)
                        if (checked) {
                          setAssignedTo(null)
                          setNewAssignment(true)
                        }
                      }}
                    />
                    {!disableChanges && (
                      <>
                        <Divider sx={{ opacity: 0.6 }} />
                        <StaffCanBookSwitch
                          checked={staffCanBook}
                          onChange={(checked) => {
                            setChangesPresent(true)
                            setStaffCanBook(checked)
                          }}
                          caption="Advertise on Available Shifts and accept applications"
                        />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Box>
                      <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} sm={5} md={4}>
                          <TextField
                            label="Number of shifts"
                            type="number"
                            fullWidth
                            size="small"
                            helperText={
                              isMultiCreate
                                ? 'Identical copies — same times, role and pay'
                                : 'Need more than one person? Enter how many'
                            }
                            inputProps={{
                              min: 1,
                              max: MAX_SHIFTS_PER_CREATE,
                              'aria-label': 'Number of shifts',
                            }}
                            value={quantity}
                            onChange={(e) => setShiftQuantity(e.target.value)}
                            onWheel={(e) => e.target.blur()}
                          />
                        </Grid>
                        <Grid item xs={12} sm={7} md={8}>
                          <StaffCanBookSwitch
                            checked={staffCanBook}
                            onChange={(checked) => {
                              setChangesPresent(true)
                              setStaffCanBook(checked)
                            }}
                            caption={
                              isMultiCreate
                                ? 'Open slots appear as one listing on Available Shifts and accept applications'
                                : 'Advertise on Available Shifts and accept applications'
                            }
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Divider sx={{ opacity: 0.6 }} />

                    {isMultiCreate ? (
                      <ShiftSlotAssignments
                        slots={slots}
                        onChange={(next) => {
                          setChangesPresent(true)
                          setSlots(next)
                        }}
                        issuesBySlot={issuesBySlot}
                        onClearIssues={clearSlotIssues(
                          setShiftAssignmentBlocked,
                        )}
                        onAssign={(value) =>
                          suggestRoleFrom(value, { overwrite: false })
                        }
                      />
                    ) : (
                      <SlotAssignmentFields
                        slot={slots[0]}
                        issues={singleSlotIssues}
                        onClearIssues={() => setShiftAssignmentBlocked(null)}
                        onAssigneeChange={(e, value) => {
                          setChangesPresent(true)
                          setSlots((prev) =>
                            resizeSlots(
                              [
                                {
                                  assignedTo: value ?? null,
                                  assignedToSubcontractor: false,
                                },
                                ...prev.slice(1),
                              ],
                              quantity,
                            ),
                          )
                          suggestRoleFrom(value, { overwrite: true })
                        }}
                        onSubcontractorToggle={(checked) => {
                          setChangesPresent(true)
                          setSlots((prev) =>
                            resizeSlots(
                              [
                                {
                                  assignedTo: checked
                                    ? null
                                    : (prev[0]?.assignedTo ?? null),
                                  assignedToSubcontractor: checked,
                                },
                                ...prev.slice(1),
                              ],
                              quantity,
                            ),
                          )
                        }}
                      />
                    )}
                  </>
                )}
              </Section>
            </Grid>

            {/* Repeat (add mode only) */}
            {!hasExistingShift && (
              <Grid item xs={12} display="flex">
                <Section
                  icon={<RepeatIcon fontSize="small" />}
                  title="Repeat"
                  caption={
                    isRepeating
                      ? 'The same shift on every matching day, each saved as its own shift'
                      : 'Need this shift on several days? Repeat it across the event'
                  }
                  action={
                    <Switch
                      checked={repeat.enabled}
                      onChange={(e) =>
                        setRepeat((prev) => ({
                          ...prev,
                          enabled: e.target.checked,
                        }))
                      }
                      inputProps={{ 'aria-label': 'Repeat this shift' }}
                    />
                  }
                >
                  {isRepeating && (
                    <RepeatShiftPanel
                      start={startTime}
                      eventEnd={eventMeta?.endDate}
                      repeat={repeat}
                      setRepeat={setRepeat}
                      occurrences={occurrences}
                      slots={slots}
                      quantity={quantity}
                      issuesByOccurrence={repeatIssuesByOccurrence}
                      onClearIssues={(occurrenceIndex, slotIndex) =>
                        setRepeatIssues((prev) => {
                          const remaining = (prev ?? []).filter(
                            (issue) =>
                              (issue?.occurrence ?? 0) !== occurrenceIndex ||
                              (issue?.slot ?? 0) !== slotIndex,
                          )
                          return remaining.length ? remaining : null
                        })
                      }
                    />
                  )}
                </Section>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* ---- Footer ---- */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            px: { xs: 2, sm: 3 },
            py: 1.5,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            spacing={1.5}
          >
            <Typography variant="caption" color="text.secondary">
              {footerCaption}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={closePopup} color="inherit">
                Cancel
              </Button>
              {!lockedAlert && (
                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={submitDisabled}
                >
                  {saving
                    ? 'Saving…'
                    : hasExistingShift
                      ? 'Save changes'
                      : createCount > 1
                        ? `Add ${createCount} shifts`
                        : 'Add shift'}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Outside the form Box: these dialogs are portalled, and React portals
          bubble events through the React tree — nested inside the shift form,
          confirming a removal (its own <form>) would also fire handleAddShift,
          re-saving the stale assignee and undoing the removal */}
      <ConfirmRemoveStaff
        open={confirmRemoveStaff}
        onClose={() => {
          setConfirmRemoveStaff(false)
          setAddNew(false)
        }}
        existingShift={existingShift}
        setAssignedTo={setAssignedTo}
        loadEvent={loadEvent}
      />

      {/* ---- Duplicate dialog ---- */}
      <Dialog
        open={openDuplicateShiftPopup}
        onClose={() => !duplicating && setOpenDuplicateShiftPopup(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.5,
                bgcolor: 'anMuted.main',
                color: 'contrast.main',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <ContentCopyOutlinedIcon fontSize="small" />
            </Box>
            <Box minWidth={0}>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                Duplicate shift
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {[
                  existingShift?.clinicalRole,
                  existingShift?.start
                    ? moment(existingShift.start).format('ddd D MMM')
                    : null,
                  existingShift?.start && existingShift?.end
                    ? `${moment(existingShift.start).format('HH:mm')} – ${moment(existingShift.end).format('HH:mm')}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1.5}>
            <Typography variant="body2" color="text.secondary">
              Copies keep the same times, role and pay
              {changesPresent ? ' as the saved version of this shift' : ''}.
              Assign any of them now, or leave them open as vacancies.
            </Typography>
            <TextField
              type="number"
              label="Number of copies"
              size="small"
              inputProps={{ min: 1, max: MAX_SHIFTS_PER_CREATE }}
              value={duplicateNumber}
              onChange={(e) => setDuplicateCount(e.target.value)}
              onWheel={(e) => e.target.blur()}
              sx={{ maxWidth: 220 }}
              autoFocus
            />
            {duplicateNumber > 1 ? (
              <ShiftSlotAssignments
                slots={duplicateSlots}
                onChange={setDuplicateSlots}
                disabled={duplicating}
                issuesBySlot={duplicateIssuesBySlot}
                onClearIssues={clearSlotIssues(setDuplicateIssues)}
              />
            ) : (
              <SlotAssignmentFields
                slot={duplicateSlots[0]}
                dense
                disabled={duplicating}
                issues={duplicateIssuesBySlot[0]}
                onClearIssues={() => setDuplicateIssues(null)}
                onAssigneeChange={(e, value) =>
                  setDuplicateSlots([
                    {
                      assignedTo: value ?? null,
                      assignedToSubcontractor: false,
                    },
                  ])
                }
                onSubcontractorToggle={(checked) =>
                  setDuplicateSlots((prev) => [
                    {
                      assignedTo: checked
                        ? null
                        : (prev[0]?.assignedTo ?? null),
                      assignedToSubcontractor: checked,
                    },
                  ])
                }
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenDuplicateShiftPopup(false)}
            color="inherit"
            disabled={duplicating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDuplicateShift}
            disabled={duplicating}
          >
            {duplicating
              ? 'Duplicating…'
              : duplicateNumber > 1
                ? `Create ${duplicateNumber} copies`
                : 'Create copy'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}

export default AddNewShift
